import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "./ThunderCash.css";
import { bofAudio, AUDIO_KEYS } from "../ballsOfFire/audio";
import { Volume2, VolumeX, Sliders, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { announceSpin } from "../../utils/spinBreakdown";
import { DailySuperChip } from "../../components/DailySuperMeter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOF = `${API}/games/thunder-cash`;
const BOF_ONLINE = `${BOF}/online`;           // authenticated staging endpoints
axios.defaults.withCredentials = true;         // send BETOHALICS JWT cookie
const SS_KEY = "thunder_cash_online_session";           // survive browser refresh/reconnect
const ART = "/games/thunder-cash";

const SYMS = ["WILD", "CROWN", "CASH", "DIAMOND", "A", "K", "Q", "J", "BALL", "SCATTER"];
const SPECIAL = new Set(["WILD", "SCATTER", "BALL"]); // bigger + slower dramatic landing
const KEY = new Set(["SCATTER", "BALL"]); // feature-triggering symbols — biggest + glow
const IMG = {
  WILD: "sym-wild.png", CROWN: "sym-crown.png", CASH: "sym-cash.png", DIAMOND: "sym-diamond.png",
  A: "sym-a.png", K: "sym-k.png", Q: "sym-q.png", J: "sym-j.png", BALL: "sym-ball.png", SCATTER: "sym-scatter.png",
};
const NAME = {
  WILD: "THUNDER KING WILD", CROWN: "LIGHTNING CROWN", CASH: "LIGHTNING CASH", DIAMOND: "STORM DIAMOND",
  A: "TRIPLE BAR", K: "DOUBLE BAR", Q: "SINGLE BAR", J: "CHERRIES", BALL: "POWER ORB", SCATTER: "SCATTER",
};
const symUrl = (s) => `${ART}/${IMG[s]}`;
const uuid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const fmt = (n) => Math.round(n).toLocaleString("en-US");
const usd = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const randSym = () => SYMS[Math.floor(Math.random() * SYMS.length)];
const randGrid = () => Array.from({ length: 5 }, () => Array.from({ length: 4 }, randSym));

// Preload every symbol texture ONCE so the fast-spinning reels never flash a
// blank cell while the browser fetches an image on its first appearance.
if (typeof Image !== "undefined") {
  [...SYMS.map(symUrl), `${ART}/bb-ball.png`].forEach((u) => { const im = new Image(); im.src = u; });
}

// Every image the game renders — warmed by the loading screen so nothing
// fetches (and lags) mid-play. vault-bg lives under PUBLIC_URL.
const ALL_ART = [
  ...SYMS.map(symUrl),
  `${ART}/bb-ball.png`, `${ART}/cabinet.png`, `${ART}/logo.png`, `${ART}/mascot.png`,
  `${ART}/jackpot-strip.png`, `${ART}/meter-balance.png`, `${ART}/meter-bet.png`, `${ART}/meter-win.png`,
  `${ART}/btn-info.png`, `${ART}/btn-minus.png`, `${ART}/btn-plus.png`, `${ART}/btn-maxbet.png`,
  `${ART}/btn-auto.png`, `${ART}/btn-spin.png`, `${ART}/bb-header.png`, `${ART}/bb-cabinet.png`,
  `${ART}/bb-locked-row.png`, `${ART}/fg-intro.png`, `${ART}/fg-counter.png`, `${ART}/fg-cabinet.png`,
  `${ART}/fg-retrigger.png`, `${ART}/fg-featurewin.png`,
  `${typeof process !== "undefined" && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : ""}/games/thunder-cash/vault-bg.jpg`,
];

// cosmetic grid sampled from the AUTHORITATIVE reel strips (guarantees only
// real symbols land — Free Games strips contain no J per M10).
function gridFromStrips(strips, rows) {
  if (!strips || !strips.length) return null;
  return strips.map((reel) => {
    const start = Math.floor(Math.random() * reel.length);
    return Array.from({ length: rows }, (_, r) => reel[(start + r) % reel.length]);
  });
}

const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
const REEL_TAIL = 12; // random symbols scrolled above the final 4 before a reel lands
// Thunder Respins respin roll: a seamless strip of ALL symbols (WILD, CROWN, CASH,
// BALL, etc.) so the empty cells spin like reels; only fireballs actually stick.
const bbRollSyms = () => { const a = Array.from({ length: 5 }, randSym); return [...a, ...a]; };
// stable per-column roll strips (built ONCE). Reusing them every render stops
// the spinning symbols from flickering as the board re-renders during a respin.
const BB_ROLL_STRIPS = Array.from({ length: 5 }, bbRollSyms);
// resting (non-ball) symbols so a Thunder Respins cell is NEVER blank after a spin
const BB_REG_SYMS = ["WILD", "CROWN", "CASH", "DIAMOND", "A", "K", "Q", "J"];
// keep already-assigned filler objects STABLE; only assign a new one to a newly
// exposed empty cell so a settled cell never changes until the next respin.
const bbFillers = (board, prev = {}) => {
  const taken = new Set(board.map((b) => `${b.col}-${b.row}`));
  const m = {};
  for (let col = 0; col < 5; col++) for (let row = 0; row < 8; row++) {
    const k = `${col}-${row}`;
    if (taken.has(k)) continue;
    m[k] = prev[k] || BB_REG_SYMS[Math.floor(Math.random() * BB_REG_SYMS.length)];
  }
  return m;
};

// A single smooth vertical-scrolling reel (WAAPI), matching the Lucky Flamingo
// feel: blur-cruise while awaiting the server, then a velocity-continuous
// decel to a DEAD STOP on the final 4 symbols. Idle it renders the final column.
const BofReel = React.forwardRef(function BofReel({ col, winRows, reelIndex, rows = 4 }, ref) {
  const viewRef = useRef(null);
  const stripRef = useRef(null);
  const animRef = useRef(null);
  const [strip, setStrip] = useState(null);
  const [phase, setPhaseState] = useState(null);  // 'blur' | 'settle' | null
  const phaseRef = useRef(null);
  const setPhase = (p) => { phaseRef.current = p; setPhaseState(p); };
  const cellH = () => (viewRef.current ? viewRef.current.clientHeight / rows : 0);

  React.useImperativeHandle(ref, () => ({
    startBlur() {
      const n = rows + 1;
      const set = Array.from({ length: n }, randSym);
      setPhase("blur");
      setStrip([...set, ...set]); // 2n rows; seamless loop over n
      // Retry across frames until React has committed the strip DOM (stripRef set)
      // and the reel has a measurable height. A single rAF can fire BEFORE the
      // commit paints, leaving the reel visually frozen ("sometimes it doesn't move").
      let tries = 0;
      const begin = () => {
        if (phaseRef.current !== "blur") return; // settle/stop took over — abort
        const h = cellH();
        if (!stripRef.current || !h) {
          if (tries++ < 20) requestAnimationFrame(begin);
          return;
        }
        if (animRef.current) animRef.current.cancel();
        animRef.current = stripRef.current.animate(
          [{ transform: `translate3d(0, ${-n * h}px, 0)` }, { transform: "translate3d(0, 0px, 0)" }],
          { duration: 260, iterations: Infinity, easing: "linear" },
        );
      };
      requestAnimationFrame(() => requestAnimationFrame(begin));
    },
    stop() {
      if (animRef.current) { animRef.current.cancel(); animRef.current = null; }
      setPhase(null); setStrip(null);
    },
    // "slam stop": if a reel is mid-settle, cancel and snap to its final symbols
    // instantly (the idle render already holds the final grid). No-op during the
    // pre-result blur (nothing to land on yet) — doSpin handles that case.
    finishNow() {
      if (phaseRef.current === "settle" && animRef.current) {
        animRef.current.cancel(); animRef.current = null;
        setPhase(null); setStrip(null);
      }
    },
    async settle(finalCol, { delay = 0, settleMs = 460 } = {}) {
      if (delay) await sleep(delay);
      const h = cellH() || 1;
      const tail = Array.from({ length: REEL_TAIL }, randSym);
      setPhase("settle"); // motion blur clears the instant we start decelerating → crisp land
      setStrip([...finalCol, ...tail]); // final rows on top, random tail below
      await nextFrame();
      if (animRef.current) { animRef.current.cancel(); animRef.current = null; }
      const el = stripRef.current;
      if (!el) { setPhase(null); setStrip(null); return; }

      const startY = -(REEL_TAIL * h);
      const ov = Math.min(h * 0.12, 14);           // small overshoot → single crisp snap
      const returnMs = Math.round(settleMs * 0.22);
      const travelMs = settleMs - returnMs;
      const distance = -startY + ov;
      const tA = Math.min(travelMs * 0.18, 100);
      const tD = travelMs * 0.5;
      const tC = Math.max(0, travelMs - tA - tD);
      const v = distance / (travelMs - 0.5 * (tA + tD));
      const dA = 0.5 * v * tA, dC = v * tC;
      const SAMPLES = Math.max(30, Math.round(travelMs / 16));
      const kf = [];
      for (let s = 0; s <= SAMPLES; s++) {
        const t = (travelMs * s) / SAMPLES;
        let tr;
        if (t <= tA) tr = 0.5 * (v / tA) * t * t;
        else if (t <= tA + tC) tr = dA + v * (t - tA);
        else { const td = t - tA - tC; tr = dA + dC + (v * td - 0.5 * (v / tD) * td * td); }
        const y = startY + tr;
        kf.push({ transform: `translate3d(0, ${y}px, 0)`, offset: Math.min(t / settleMs, 0.999), easing: "linear" });
      }
      // back-out snap: ease the overshoot (+ov) precisely back to 0 (no bounce past target)
      kf[kf.length - 1].easing = "cubic-bezier(0.22, 0.61, 0.36, 1)";
      kf.push({ transform: "translate3d(0, 0px, 0)", offset: 1, easing: "linear" });

      const anim = el.animate(kf, { duration: settleMs, fill: "forwards" });
      animRef.current = anim;
      try { await anim.finished; } catch (e) { /* cancelled */ }
      animRef.current = null;
      setPhase(null); setStrip(null);
    },
  }), []);

  if (strip) {
    return (
      <div className="bof-reel" ref={viewRef} data-testid={`bof-reel-${reelIndex}`}>
        <div className={`bof-reel-strip${phase === "blur" ? " blur" : ""}`} ref={stripRef} style={{ "--bof-cellh": `${cellH()}px` }}>
          {strip.map((s, k) => (
            <div className="bof-strip-cell" key={k}>
              <img src={symUrl(s)} alt="" draggable={false} decoding="sync" loading="eager" className={[SPECIAL.has(s) ? "bof-special" : "", KEY.has(s) ? "tc-key" : ""].filter(Boolean).join(" ") || undefined} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="bof-reel" ref={viewRef} data-testid={`bof-reel-${reelIndex}`}>
      {(col || []).map((s, row) => {
        const isWin = !!(winRows && winRows[row]);
        const sp = SPECIAL.has(s);
        return (
          <div key={row} className={`bof-cell${isWin ? " win" : ""}${sp ? " special" : ""}`} data-testid={`bof-cell-${reelIndex}-${row}`}>
            <img src={symUrl(s || "A")} alt={s} decoding="sync" loading="eager" className={[sp ? "bof-special" : "", KEY.has(s) ? "tc-key" : ""].filter(Boolean).join(" ") || undefined} />
          </div>
        );
      })}
    </div>
  );
});

// Cumulative Thunder Respins board state up to (and including) a step index.
// Pure derivation from the AUTHORITATIVE backend trace — no invented values.
function bbStateUpTo(trace, stepIdx) {
  const board = [];
  let activeRows = trace.start_active_rows;
  let respins = 3;
  let unlocked = [];
  for (let i = 0; i <= stepIdx && i < trace.steps.length; i++) {
    const s = trace.steps[i];
    if (s.type === "init" || s.type === "land") s.balls.forEach((b) => board.push(b));
    activeRows = s.active_rows;
    respins = s.respins;
    unlocked = s.type === "land" ? (s.unlocked_rows || []) : [];
  }
  return { board, activeRows, respins, unlocked };
}

const BET_LEVELS = [0.5, 1, 2, 5, 10, 20]; // dollars (wallet: credits + bonus_credits)
const MAX_WIN_DISPLAY = 5000; // matches backend MAX_WIN_PER_ROUND — never SHOW a regular win over this (jackpots shown separately)
const START_CREDITS = 100000;

// coin/ping cue (synth) used by the burst finale + jackpot count-up
const playSfx = () => bofAudio.sfxCoin();

// Big-win tiers (multiple of the current bet) + celebratory ember particles.
const winTier = (win, bet) => {
  const x = win / (bet || 50);
  if (x >= 50) return { label: "MEGA WIN", cls: "mega" };
  if (x >= 25) return { label: "HUGE WIN", cls: "huge" };
  if (x >= 10) return { label: "BIG WIN", cls: "big" };
  return null;
};
const BIG_WIN_EMBERS = Array.from({ length: 22 }, (_, i) => ({
  id: i, left: Math.random() * 100, delay: Math.random() * 0.6,
  dur: 1.3 + Math.random() * 1.2, size: 8 + Math.random() * 16,
  drift: (Math.random() - 0.5) * 70,
}));

export default function ThunderCash() {
  const [config, setConfig] = useState(null);
  const [bootError, setBootError] = useState(false);
  const [offline, setOffline] = useState(false); // game turned OFF by operator (admin BOF Settings)
  const navigate = useNavigate();
  const goLobby = useCallback(() => { try { bofAudio.stopAll(); } catch (e) {} navigate("/casino/slots"); }, [navigate]);
  const [sessionId, setSessionId] = useState(null);
  const [balance, setBalance] = useState(0);
  const [bonus, setBonus] = useState(0); // playable bonus portion (spent before cashable credits)
  const [boltZap, setBoltZap] = useState(false); // mascot cash-orb → thunderbolt celebration on bonus trigger

  // Thunder Cash mix (owner request Jun 2026): music master ALL THE WAY DOWN.
  // All Thunder Cash music (main-screen loop + bonus + free-games tracks) is
  // silenced so only SFX + the voiceovers play. Ducking no longer flutters the
  // mix, so sound effects read consistently. Scoped to this game only (base key
  // + scales restored on unmount) so Balls of Fire audio is untouched.
  useEffect(() => {
    bofAudio.baseKey = "tcMusic";   // main-screen loop = supplied Thunder Cash track
    bofAudio.baseVolScale = 0;      // main music OFF (all the way down)
    bofAudio.musicScale = 0;        // bonus + free-games music OFF too
    bofAudio.sfxScale = 1.35;       // SFX noticeably louder
    bofAudio._applyMusic();
    return () => {
      bofAudio.baseKey = "bbMusic"; bofAudio.baseVolScale = 1;
      bofAudio.musicScale = 1; bofAudio.sfxScale = 1; bofAudio._applyMusic();
    };
  }, []);
  const [betIndex, setBetIndex] = useState(0);
  const [grid, setGrid] = useState(randGrid());
  const [spinning, setSpinning] = useState(false);
  const [winCells, setWinCells] = useState(new Set());
  const [winLines, setWinLines] = useState([]);
  const [lastWin, setLastWin] = useState(0);
  const [totalWin, setTotalWin] = useState(0);
  const [toast, setToast] = useState(null);
  const [showPaytable, setShowPaytable] = useState(false);
  const [feature, setFeature] = useState(null); // {type, ...}
  const [auto, setAuto] = useState(false);
  const autoRef = useRef(false);
  const bootRef = useRef(false);
  const reelRefs = useRef([]);
  const fgReelRefs = useRef([]);
  const stopRef = useRef(false);
  const [stopArmed, setStopArmed] = useState(false);
  const [bigWin, setBigWin] = useState(null); // { amount, label, cls, shown }
  const [jackpot, setJackpot] = useState(null); // { tier, amount, shown } — jackpot celebration
  const [jpPools, setJpPools] = useState(null); // live shared progressive pools {MINI:current,...}
  const [audioMuted, setAudioMuted] = useState(bofAudio.getState().muted);
  const [audioPanel, setAudioPanel] = useState(false);
  const [loading, setLoading] = useState(true);   // preload gate (all art + audio)
  const [loadPct, setLoadPct] = useState(0);
  const [fgWin, setFgWin] = useState({ cells: new Set(), lines: [] }); // free-games win glow
  const bet = BET_LEVELS[betIndex];

  useEffect(() => {
    if (bootRef.current) return;   // guard React StrictMode double-mount → one session
    bootRef.current = true;
    (async () => {
      try {
        const { data: cfg } = await axios.get(`${BOF}/config`);
        setConfig(cfg);
        // Authenticated online staging: recover an existing session on refresh,
        // else start a fresh one. Balance/state are backend-authoritative.
        const existing = sessionStorage.getItem(SS_KEY);
        if (existing) {
          try {
            const { data: st } = await axios.get(`${BOF_ONLINE}/state`, { params: { session_id: existing } });
            if (st && !st.closed) {
              setSessionId(existing);
              setBalance(st.balance);
              setBonus(st.bonus || 0);
              return;
            }
          } catch (_) { sessionStorage.removeItem(SS_KEY); }
        }
        const { data: s } = await axios.post(`${BOF_ONLINE}/session/start`, {});
        setSessionId(s.session_id);
        setBalance(s.balance);
        setBonus(s.bonus || 0);
        sessionStorage.setItem(SS_KEY, s.session_id);
      } catch (e) {
        if (e?.response?.status === 403) { setOffline(true); }
        else { setBootError(true); setToast("Failed to load game"); }
      }
    })();
  }, []);

  // Loading screen: warm EVERY image + the 4 audio tracks up front so nothing
  // fetches (and stutters) during play. Bar fills over ~MIN and only dismisses
  // once assets are ready AND the minimum has elapsed (hard cap at MAX).
  const doneRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const t0 = performance.now();
    const MIN = 11000, MAX = 20000;
    const audioUrls = Object.values(AUDIO_KEYS || {});
    const total = ALL_ART.length + audioUrls.length;
    doneRef.current = 0;
    const tick = () => { doneRef.current += 1; };
    ALL_ART.forEach((u) => { const im = new Image(); im.onload = tick; im.onerror = tick; im.src = u; });
    audioUrls.forEach((u) => {
      const a = new Audio(); a.preload = "auto";
      a.oncanplaythrough = tick; a.onerror = tick; a.src = u;
      try { a.load(); } catch (e) { /* noop */ }
    });
    const iv = setInterval(() => {
      if (cancelled) return;
      const elapsed = performance.now() - t0;
      const assetsDone = doneRef.current >= total;
      const finish = (assetsDone && elapsed >= MIN) || elapsed >= MAX;
      setLoadPct(finish ? 100 : Math.round(Math.min(99, (elapsed / MIN) * 100)));
      if (finish) { clearInterval(iv); setLoading(false); }
    }, 150);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Auto-start the base track on the game screen (no spin needed) — but NOT until
  // the loading screen is done. Browsers may block autoplay, so we also unlock on
  // the first user gesture (gated so nothing plays while the loader is up).
  const loadingRef = useRef(true);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => {
    bofAudio.primeBase(); // best-effort: unlock silently within the lobby-click activation
    const onGesture = () => { if (loadingRef.current) bofAudio.primeBase(); else bofAudio.kick(); };
    const opts = { passive: true };
    window.addEventListener("pointerdown", onGesture, opts);
    window.addEventListener("touchstart", onGesture, opts);
    window.addEventListener("keydown", onGesture);
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, []);

  // ---- Thunder Cash voiceover -------------------------------------------
  // A dedicated element (introVoxRef) is UNLOCKED during the loading screen
  // (inside the lobby-click activation) so it can AUTOPLAY the instant the game
  // opens — no spin needed. A fresh/debounced player is used for the bonus intros
  // (audio is already unlocked by then).
  const lastIntroRef = useRef(0);
  const introVoxRef = useRef(null);

  // Fresh, debounced VO — used at the START of each bonus (and as a fallback).
  const playIntroVoiceRef = useRef(() => Promise.resolve(false));
  playIntroVoiceRef.current = (onEnded) => {
    const now = Date.now();
    if (now - lastIntroRef.current < 1500) return Promise.resolve(false); // debounce
    lastIntroRef.current = now;
    const st = bofAudio.getState();
    const vox = new Audio(`${ART}/intro-voice.m4a`);
    vox.preload = "auto";
    vox.volume = st.muted ? 0 : Math.min(1, Math.max(0.75, st.sfxVol));
    if (onEnded) vox.addEventListener("ended", onEnded, { once: true });
    try {
      const p = vox.play();
      if (p && p.then) return p.then(() => true).catch(() => {
        if (onEnded) { try { vox.removeEventListener("ended", onEnded); } catch (e) {} }
        return false;
      });
    } catch (e) {}
    return Promise.resolve(true);
  };

  // Silently unlock the open-VO element within a user gesture (play@0 → pause).
  const primeIntroRef = useRef(() => {});
  primeIntroRef.current = () => {
    if (!introVoxRef.current) {
      const a = new Audio(`${ART}/intro-voice.m4a`); a.preload = "auto"; introVoxRef.current = a;
    }
    const a = introVoxRef.current;
    if (a._primed) return;
    try {
      a.volume = 0;
      const p = a.play();
      if (p && p.then) p.then(() => { try { a.pause(); a.currentTime = 0; a._primed = true; } catch (e) {} }).catch(() => {});
    } catch (e) {}
  };

  // Play the primed open-VO element WITH sound, from the start.
  const playPrimedIntroRef = useRef(() => Promise.resolve(false));
  playPrimedIntroRef.current = (onEnded) => {
    const a = introVoxRef.current;
    if (!a) return Promise.resolve(false);
    const st = bofAudio.getState();
    a.volume = st.muted ? 0 : Math.min(1, Math.max(0.75, st.sfxVol));
    try { a.currentTime = 0; } catch (e) {}
    if (onEnded) a.addEventListener("ended", onEnded, { once: true });
    try {
      const p = a.play();
      if (p && p.then) return p.then(() => true).catch(() => {
        if (onEnded) { try { a.removeEventListener("ended", onEnded); } catch (e) {} }
        return false;
      });
    } catch (e) {}
    return Promise.resolve(true);
  };

  // Prime the open-VO during the loading screen (on mount + any gesture while the
  // loader is up), mirroring how primeBase() unlocks the music.
  useEffect(() => {
    primeIntroRef.current();
    const onGesture = () => { if (loadingRef.current) primeIntroRef.current(); };
    const opts = { passive: true };
    window.addEventListener("pointerdown", onGesture, opts);
    window.addEventListener("touchstart", onGesture, opts);
    window.addEventListener("keydown", onGesture);
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, []);

  // When the game opens, play the Thunder Cash intro voiceover FIRST — from the
  // element primed during loading, so it fires WITHOUT a spin — then ramp the
  // music up only AFTER the voice ends. If autoplay is still blocked, the next
  // tap fires it.
  const introRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    const startMusic = () => bofAudio.baseMusic();
    let teardown = () => {};
    const tryOpen = async () => {
      if (introRef.current) return;
      let ok = await playPrimedIntroRef.current(startMusic);
      if (!ok) ok = await playIntroVoiceRef.current(startMusic);
      if (ok) { introRef.current = true; teardown(); setTimeout(startMusic, 4500); }
    };
    const onGesture = () => tryOpen();
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("touchstart", onGesture, { passive: true });
    window.addEventListener("keydown", onGesture);
    teardown = () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
    tryOpen();
    return teardown;
  }, [loading]); // eslint-disable-line


  // approval preview of the AUTHORITATIVE feature (?demo=burning&trigger=&seed=&step=).
  // The timeline/ball values/respins come from the backend trace, never hardcoded.
  const demoRef = useRef(false);
  useEffect(() => {
    if (!config || demoRef.current) return;
    const p = new URLSearchParams(window.location.search);
    const demo = p.get("demo");
    if (!demo) return;
    demoRef.current = true;
    (async () => {
      if (demo === "burning") {
        const trigger = Number(p.get("trigger") || 20);
        const seed = Number(p.get("seed") || 7);
        const step = p.get("step"); // null → animate; number → static step; 'final'
        try {
          const { data: trace } = await axios.get(
            `${BOF}/burning-demo?trigger_balls=${trigger}&seed=${seed}`);
          setToast("THUNDER RESPINS BONUS!");
          if (step === null || step === undefined) {
            await runBurningBalls(trace);
          } else if (step === "final") {
            showBurningStatic(trace, trace.steps.length - 1, true);
          } else {
            showBurningStatic(trace, Number(step), false);
          }
        } catch (e) { setToast("demo failed"); }
      }
      if (demo === "free") {
        const scatters = Number(p.get("scatters") || 4);
        const seed = Number(p.get("seed") || 7);
        const step = p.get("step"); // null → animate; number → spin index; 'intro'/'win'
        try {
          const { data: trace } = await axios.get(`${BOF}/free-demo?scatters=${scatters}&seed=${seed}`);
          setToast(`${trace.initial_games} FREE GAMES!`);
          if (step === null || step === undefined) await runFreeGames(trace);
          else showFreeStatic(trace, step);
        } catch (e) { setToast("demo failed"); }
      }
      if (demo === "bigwin") {
        // QA preview of the celebration only (?demo=bigwin&tier=big|huge|mega&amount=)
        const cls = p.get("tier") || "mega";
        const amount = Number(p.get("amount") || (cls === "mega" ? 5000 : cls === "huge" ? 2000 : 800));
        const label = cls === "mega" ? "MEGA WIN" : cls === "huge" ? "HUGE WIN" : "BIG WIN";
        await sleep(400);
        while (true) { await runBigWin(amount, { label, cls }); await sleep(500); } // loop for easy viewing
      }
      if (demo === "jackpot") {
        // QA preview of the jackpot celebration (?demo=jackpot&tier=mini|minor|major|mega)
        const tier = (p.get("tier") || "mega").toUpperCase();
        await sleep(400);
        while (true) { await runJackpot(tier); await sleep(600); } // loop for easy viewing
      }
    })();
  }, [config]); // eslint-disable-line

  // Render a STATIC Thunder Respins state (for QA screenshots). Holds; no auto-clear.
  const showBurningStatic = useCallback((trace, stepIdx, withTotal) => {
    const st = bbStateUpTo(trace, stepIdx);
    setFeature({
      type: "burning",
      board: st.board,
      activeRows: st.activeRows,
      respins: st.respins,
      unlocking: st.unlocked,
      landing: new Set(),
      total: withTotal ? Math.round(trace.total_x * bet) : null,
    });
  }, [bet]);

  const jackpotVal = useCallback((tier) => {
    if (!config) return 0;
    return (config.burning_balls.jackpots_x[tier] || 0) * bet;
  }, [config, bet]);

  // Jackpot celebration (presentation ONLY). Tier + amount come from the frozen
  // engine (ball.jackpot / full_board); the amount is jackpots_x[tier] × bet.
  const runJackpot = useCallback(async (tier, explicitAmount) => {
    const amount = explicitAmount != null ? explicitAmount
      : (jpPoolsRef.current?.[tier]?.current ?? jackpotVal(tier));
    setJackpot({ tier, amount, shown: 0 });
    playSfx();
    const dur = tier === "MEGA" ? 2200 : 1400;
    const start = performance.now();
    await new Promise((res) => {
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        setJackpot((j) => (j ? { ...j, shown: Math.round(amount * e) } : j));
        if (p < 1) requestAnimationFrame(step); else res();
      };
      requestAnimationFrame(step);
    });
    await sleep(tier === "MEGA" ? 2400 : 1600);
    setJackpot(null);
  }, [jackpotVal]);

  const revealWinLines = useCallback(async (lineWins) => {
    const cells = new Set();
    lineWins.forEach((lw) => lw.positions.forEach(([c, r]) => cells.add(`${c}-${r}`)));
    setWinCells(cells);
    setWinLines(lineWins.map((lw) => lw.positions));
  }, []);

  // Drive the Thunder Respins presentation from the AUTHORITATIVE backend trace.
  // Every ball, value, jackpot, respin reset/decrement and row-unlock comes from
  // `trace.steps`. The headline total is trace.total_x × bet (server-authoritative).
  const runBurningBalls = useCallback(async (trace, jackpotAwards) => {
    if (!trace || !trace.steps || !trace.steps.length) return;
    // Thunder Cash intro voiceover at the top of the bonus (no branded BOF voice).
    playIntroVoiceRef.current();
    bofAudio.playMusic("freeMusic", { fadeIn: 500 }); // start bonus music NOW so it plays the WHOLE bonus (even short 1-2 respin ones)
    await sleep(4000);             // 3–5s pause before the bonus so entering it doesn't feel rushed
    // map tier -> actual awarded amount (progressive pool) from the settlement
    const jpAmt = {};
    (jackpotAwards || []).forEach((a) => { jpAmt[a.tier] = a.amount; });
    const s0 = trace.steps[0];
    let board = [];
    // reveal a respin by STOPPING COLUMNS LEFT→RIGHT, one after another (smooth,
    // like base reels) — columns keep spinning until their turn to land.
    const revealCols = async (newBalls, spin = 460) => {
      const landing = new Set();
      // keep locked-row fillers stable; drop active-row fillers so THIS respin
      // lands fresh objects (they're hidden behind the roll until each col stops).
      setFeature((f) => {
        const activeFrom = 8 - (f.activeRows || 0);
        const kept = {};
        Object.keys(f.fillers || {}).forEach((k) => {
          const r = Number(k.split("-")[1]);
          if (r < activeFrom) kept[k] = f.fillers[k];
        });
        return { ...f, rolling: true, rollCol: 0, settleCol: -1, fillers: kept, landing: new Set(), unlocking: [] };
      });
      await sleep(spin);
      for (let col = 0; col < 5; col++) {
        // decelerate THIS column smoothly before it lands (no hard cut)
        setFeature((f) => ({ ...f, settleCol: col }));
        await sleep(480); // slower L→R column reveal for suspense
        const colBalls = (newBalls || []).filter((b) => b.col === col);
        colBalls.forEach((b) => { board.push(b); landing.add(`${b.col}-${b.row}`); bofAudio.playSfx("bbLock"); });
        if (colBalls.length) bofAudio.sfxBoom(); // fireball(s) land this column → boom (once per column)
        setFeature((f) => ({ ...f, rollCol: col + 1, settleCol: -1, board: board.slice(), fillers: bbFillers(board, f.fillers), landing: new Set(landing) }));
      }
      setFeature((f) => ({ ...f, rolling: false }));
      return landing;
    };

    setFeature({ type: "burning", board: [], activeRows: s0.active_rows, respins: 3,
      unlocking: [], landing: new Set(), rolling: true, rollCol: 0, settleCol: -1, fillers: {}, total: null });
    // bonus music already started above (plays through the entire bonus, short or long)
    await revealCols(s0.balls, 700);
    await sleep(880);
    setFeature((f) => ({ ...f, landing: new Set() }));

    for (let i = 1; i < trace.steps.length; i++) {
      const s = trace.steps[i];
      if (s.type === "land") {
        await revealCols(s.balls, 620);
        // a fireball landed → unlock 3 respins again
        setFeature((f) => ({ ...f, activeRows: s.active_rows, respins: 3, unlocking: s.unlocked_rows || [] }));
        await sleep(820);
        setFeature((f) => ({ ...f, landing: new Set(), unlocking: [] }));
        // celebrate any jackpot ball(s) that landed this respin (MINI/MINOR/MAJOR)
        for (const jb of s.balls.filter((b) => b.jackpot)) {
          await runJackpot(jb.jackpot, jpAmt[jb.jackpot]);
        }
      } else { // dead respin — columns spun & landed non-ball objects; decrement
        await revealCols([], 620);
        setFeature((f) => ({ ...f, respins: s.respins }));
        await sleep(460);
      }
    }
    // full board → MEGA jackpot (frozen engine adds "MEGA" to jackpots_awarded)
    if (trace.full_board) await runJackpot("MEGA", jpAmt["MEGA"]);
    await sleep(450);
    // FINALE: each fireball FLIES to the FEATURE WIN counter and its value adds
    // up as it lands. Sum of ball values == trace.total_x (authoritative); the
    // final tally is reconciled to trace.total_x × bet exactly.
    const total = Math.min(MAX_WIN_DISPLAY, Math.round(trace.total_x * bet));
    setFeature((f) => (f ? { ...f, total: 0, collecting: true, flew: new Set() } : f));
    await nextFrame();
    const overlayEl = document.querySelector('[data-testid="bof-burning-overlay"]');
    const totalEl = overlayEl && overlayEl.querySelector('[data-testid="bof-burning-total"]');
    const tRect = totalEl ? totalEl.getBoundingClientRect() : { left: window.innerWidth / 2, top: window.innerHeight - 40, width: 0, height: 0 };
    const tx = tRect.left + tRect.width / 2, ty = tRect.top + tRect.height / 2;

    const flyCoin = (r, label, isJp) => new Promise((resolve) => {
      const fly = document.createElement("div");
      fly.className = "bof-fly-coin" + (isJp ? " jp" : "");
      fly.style.left = `${r.left}px`; fly.style.top = `${r.top}px`;
      fly.style.width = `${r.width}px`; fly.style.height = `${r.height}px`;
      fly.style.backgroundImage = `url(${ART}/bb-ball.png)`;
      const span = document.createElement("span"); span.textContent = label; fly.appendChild(span);
      document.body.appendChild(fly);
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const dx = tx - cx, dy = ty - cy;
      const anim = fly.animate([
        { transform: "translate(0,0) scale(1)", opacity: 1, offset: 0 },
        { transform: `translate(${dx * 0.12}px, ${dy * 0.08 - 26}px) scale(1.28)`, opacity: 1, offset: 0.22 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.24)`, opacity: 0.85, offset: 1 },
      ], { duration: 640, easing: "cubic-bezier(0.5, 0, 0.75, 1)", fill: "forwards" });
      anim.finished.catch(() => {}).then(() => { fly.remove(); resolve(); });
    });

    // launch bottom-to-top, left-to-right for a rising cascade toward the counter
    const ordered = board.slice().sort((a, b) => (b.row - a.row) || (a.col - b.col));
    let running = 0;
    const flights = [];
    for (const b of ordered) {
      const cell = overlayEl && overlayEl.querySelector(`[data-testid="bof-bb-ball-${b.col}-${b.row}"]`);
      const rect = cell ? cell.getBoundingClientRect() : null;
      const add = Math.round(b.value_x * bet);
      const label = b.jackpot ? b.jackpot : usd(add);
      setFeature((f) => { const flew = new Set(f.flew); flew.add(`${b.col}-${b.row}`); return { ...f, flew }; });
      if (rect) {
        flights.push(flyCoin(rect, label, !!b.jackpot).then(() => {
          running += add; playSfx();
          setFeature((f) => (f ? { ...f, total: running } : f));
        }));
      } else {
        running += add;
        setFeature((f) => (f ? { ...f, total: running } : f));
      }
      await sleep(70);
    }
    await Promise.all(flights);
    setFeature((f) => (f ? { ...f, total, collecting: false } : f)); // reconcile exact
    bofAudio.baseMusic(); // BB feature over → resume base-game music
    await sleep(1500);
    setFeature(null);
  }, [bet, runJackpot]);

  // Drive Free Games from the AUTHORITATIVE backend trace. Each spin grid, win,
  // GAME X OF Y counter, retrigger and the FEATURE WIN total are live data.
  const runFreeGames = useCallback(async (trace) => {
    if (!trace || !trace.spins || !trace.spins.length) return;
    setFgWin({ cells: new Set(), lines: [] });
    setFeature({ type: "free", phase: "intro", grid: trace.spins[0].grid,
      x: 0, y: trace.initial_games, spinWin: 0, running: 0, retrigger: false, total: null });
    bofAudio.playVoice("freeVox");                       // Balls of Fire Free Games voice (per user) — Thunder Cash voice stays on Thunder Respins
    await sleep(1600);
    bofAudio.playMusic("freeMusic", { fadeIn: 400 });    // keep the Free-Games song looping underneath (incl. retriggers)
    for (const s of trace.spins) {
      setFgWin({ cells: new Set(), lines: [] }); // clear previous glow before spin
      // smooth strip spin, same feel as base play (reels blur then settle L→R)
      fgReelRefs.current.forEach((rf) => rf && rf.startBlur());
      setFeature((f) => ({ ...f, phase: "spin", x: s.index, y: s.total_awarded_after }));
      await sleep(1000); // longer visible spin → more suspense before landing
      setFeature((f) => ({ ...f, grid: s.grid }));
      // reels land LEFT→RIGHT, one after another (matches base play), slower for drama
      for (let i = 0; i < fgReelRefs.current.length; i++) {
        const rf = fgReelRefs.current[i];
        if (rf) {
          await rf.settle(s.grid[i], { delay: 0, settleMs: 620 });
          bofAudio.sfxReelStop();
          const cs = s.grid[i] || [];
          if (cs.some((x) => x === "BALL")) bofAudio.sfxBoom();      // fireball lands → boom
          if (cs.some((x) => x === "SCATTER")) bofAudio.sfxScatter(); // scatter lands → soft sparkle
        }
      }
      setFeature((f) => ({ ...f, spinWin: Math.round(s.spin_total_x * bet),
        running: Math.round(s.running_total_x * bet) }));
      // glow the winning paylines exactly like base play
      if (s.line_wins && s.line_wins.length) {
        const cells = new Set();
        s.line_wins.forEach((lw) => lw.positions.forEach(([c, r]) => cells.add(`${c}-${r}`)));
        setFgWin({ cells, lines: s.line_wins.map((lw) => lw.positions) });
        await sleep(650); // hold so the winning lines are visible
      }
      await sleep(360);
      if (s.retrigger) {
        setFeature((f) => ({ ...f, retrigger: true, retriggerAdded: s.retrigger_added }));
        await sleep(1200);
        setFeature((f) => ({ ...f, retrigger: false }));
      }
    }
    setFgWin({ cells: new Set(), lines: [] });
    await sleep(450);
    setFeature((f) => ({ ...f, phase: "win", total: Math.min(MAX_WIN_DISPLAY, Math.round(trace.total_x * bet)) }));
    bofAudio.baseMusic(); // free games over → resume base-game music
    await sleep(2600);
    setFeature(null);
  }, [bet]);

  // Static Free Games render for QA screenshots (?demo=free&...&step=intro|win|N).
  const showFreeStatic = useCallback((trace, step) => {
    if (step === "intro") {
      setFeature({ type: "free", phase: "intro", grid: trace.spins[0].grid, x: 0,
        y: trace.initial_games, spinWin: 0, running: 0, retrigger: false, total: null });
    } else if (step === "win") {
      const last = trace.spins[trace.spins.length - 1];
      setFeature({ type: "free", phase: "win", grid: last.grid, x: last.index,
        y: trace.total_awarded, spinWin: 0, running: Math.round(trace.total_x * bet),
        retrigger: false, total: Math.round(trace.total_x * bet) });
    } else {
      const s = trace.spins[Math.max(0, Number(step) - 1)];
      setFeature({ type: "free", phase: "spin", spinning: false, grid: s.grid, x: s.index,
        y: s.total_awarded_after, spinWin: Math.round(s.spin_total_x * bet),
        running: Math.round(s.running_total_x * bet), retrigger: s.retrigger,
        retriggerAdded: s.retrigger_added, total: null });
    }
  }, [bet]);

  const runBigWin = useCallback(async (amount, tier) => {
    setBigWin({ amount, label: tier.label, cls: tier.cls, shown: 0 });
    const start = performance.now();
    const dur = 1200;
    await new Promise((res) => {
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        setBigWin((b) => (b ? { ...b, shown: Math.round(amount * e) } : b));
        if (p < 1) requestAnimationFrame(step); else res();
      };
      requestAnimationFrame(step);
    });
    await sleep(1100);
    setBigWin(null);
  }, []);

  // Staging-only bonus PREVIEW: plays the exact authoritative feature (same
  // runBurningBalls/runFreeGames a live trigger uses) from the frozen-engine
  // demo traces, so both bonuses can be previewed on demand. No math change.
  const previewBonus = useCallback(async (which) => {
    if (spinning || feature) return;
    setSpinning(true); setToast(null);
    try {
      const seed = Math.floor(Math.random() * 9000) + 1;
      if (which === "burning") {
        const { data } = await axios.get(`${BOF}/burning-demo?trigger_balls=20&seed=${seed}`);
        setToast("THUNDER RESPINS BONUS!");
        await runBurningBalls(data);
      } else {
        const { data } = await axios.get(`${BOF}/free-demo?scatters=3&seed=${seed}`);
        setToast(`${data.initial_games} FREE GAMES!`);
        await runFreeGames(data);
      }
      setToast(null);
    } catch (e) { setToast("preview failed"); }
    finally { setSpinning(false); }
  }, [spinning, feature, runBurningBalls, runFreeGames]);

  const doSpin = useCallback(async () => {
    if (!sessionId || spinning) return;
    if (balance < bet) { setToast("Insufficient credits"); setAuto(false); autoRef.current = false; return; }
    stopRef.current = false;
    setSpinning(true); setStopArmed(false); setWinCells(new Set()); setWinLines([]); setLastWin(0); setToast(null);
    bofAudio.baseMusic();      // first spin (a user gesture) starts base-game music; idempotent after
    bofAudio.sfxReelStart();   // reel-spin whoosh
    // start all 5 reels blur-cruising left→right immediately (Lucky-Flamingo feel)
    reelRefs.current.forEach((rf) => rf && rf.startBlur());
    // let the player "stop" the reels shortly after they start (feels controllable)
    setTimeout(() => setStopArmed(true), 260);
    try {
      const { data } = await axios.post(`${BOF_ONLINE}/spin`, { session_id: sessionId, client_request_id: uuid(), bet });
      const r = data.result;
      setGrid(r.base_grid);
      // reels land LEFT→RIGHT, one after another (sequential). If the player hit
      // STOP, the remaining reels snap fast (still left→right).
      for (let i = 0; i < reelRefs.current.length; i++) {
        const rf = reelRefs.current[i];
        if (!rf) continue;
        const special = (r.base_grid[i] || []).some((s) => SPECIAL.has(s));
        const ms = stopRef.current ? 140 : (special ? 520 : 440);
        await rf.settle(r.base_grid[i], { delay: 0, settleMs: ms });
        bofAudio.sfxReelStop(); // click as each reel lands (left→right)
        const colSyms = r.base_grid[i] || [];
        if (colSyms.some((s) => s === "BALL")) bofAudio.sfxBoom();      // fireball lands → boom
        if (colSyms.some((s) => s === "SCATTER")) bofAudio.sfxScatter(); // scatter lands → soft sparkle
      }
      setBalance(data.balance);
      setBonus(data.bonus || 0);
      await sleep(90);
      if (r.base_line_wins.length) await revealWinLines(r.base_line_wins);
      // features in authoritative order
      for (const feat of r.features) {
        if (feat === "BURNING_BALLS") { setToast("THUNDER RESPINS BONUS!"); setBoltZap(true); try { bofAudio.sfxBoom(); bofAudio.sfxBigWin(); } catch (e) {} await sleep(1300); await runBurningBalls(r.burning, data.jackpots); setBoltZap(false); }
        if (feat === "FREE_GAMES") { setToast(`${r.free_games} FREE GAMES!`); setBoltZap(true); try { bofAudio.sfxBoom(); bofAudio.sfxBigWin(); } catch (e) {} await sleep(1300); await runFreeGames(r.free); setBoltZap(false); }
      }
      setLastWin(data.win_credits);
      announceSpin(bet, data.win_credits);
      if (data.win_credits > 0) {
        setTotalWin((t) => t + data.win_credits);
        const tier = winTier(data.win_credits, bet);
        if (tier) { bofAudio.sfxBigWin(); await runBigWin(data.win_credits, tier); setToast(null); }
        else { bofAudio.sfxWin(); setToast(`WIN ${usd(data.win_credits)}`); }
      } else setToast(null);
    } catch (e) {
      reelRefs.current.forEach((rf) => rf && rf.stop());
      setToast(e?.response?.data?.detail || "Spin failed");
      setAuto(false); autoRef.current = false;
    } finally {
      stopRef.current = false;
      setStopArmed(false);
      setSpinning(false);
    }
  }, [sessionId, spinning, balance, bet, revealWinLines, runBurningBalls, runFreeGames, runBigWin]);

  // "slam stop": snap the reels home. Fast-forwards any reel already settling;
  // reels still cruising (result not back yet) land fast the instant it arrives.
  const requestStop = useCallback(() => {
    stopRef.current = true;
    setStopArmed(false);
    reelRefs.current.forEach((rf) => rf && rf.finishNow && rf.finishNow());
  }, []);

  // autoplay loop
  useEffect(() => {
    autoRef.current = auto;
    if (!auto) return;
    let cancelled = false;
    (async () => {
      while (autoRef.current && !cancelled) {
        await doSpin();
        await sleep(600);
        if (balanceRef.current < bet) { setAuto(false); autoRef.current = false; }
      }
    })();
    return () => { cancelled = true; };
  }, [auto]); // eslint-disable-line

  const balanceRef = useRef(balance);
  useEffect(() => { balanceRef.current = balance; }, [balance]);
  const jpPoolsRef = useRef(null);
  useEffect(() => () => bofAudio.stopAll(), []); // stop all audio on unmount

  // Poll the SHARED progressive jackpot pools so the strip ticks up live as
  // anyone across BETOHALICS plays Thunder Cash (everyone sees the same values).
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${BOF}/jackpots`);
        if (alive) { setJpPools({ ...data.tiers, mega_min_bet: data.mega_min_bet }); jpPoolsRef.current = data.tiers; }
      } catch (e) { /* keep last known */ }
    };
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Live pool value for a tier (falls back to the frozen multiplier display).
  const jpDisplay = useCallback((tier) => {
    if (jpPools && jpPools[tier]) return jpPools[tier].current;
    return jackpotVal(tier);
  }, [jpPools, jackpotVal]);


  const changeBet = (dir) => {
    setBetIndex((i) => Math.max(0, Math.min(BET_LEVELS.length - 1, i + dir)));
  };
  const maxBet = () => setBetIndex(BET_LEVELS.length - 1);

  if (offline) {
    return (
      <div className="bof-root tc-root"><div className="bof-stage"><div className="bof-header" style={{ flexDirection: "column", gap: 14, padding: "60px 20px", textAlign: "center" }}>
        <h1 className="bof-gold-text" style={{ fontSize: 30 }} data-testid="bof-offline">THUNDER CASH</h1>
        <p style={{ color: "#e7c78a", fontSize: 15, maxWidth: 340 }} data-testid="bof-offline-msg">
          This game is coming soon. Please check back shortly.
        </p>
      </div></div></div>
    );
  }

  if (!config) {
    return (
      <div className="bof-root tc-root"><div className="bof-stage"><div className="bof-header" style={{ flexDirection: "column", gap: 14, padding: "40px 16px", textAlign: "center" }}>
        <h1 className="bof-gold-text" style={{ fontSize: 28 }} data-testid="bof-loading">
          {bootError ? "THUNDER CASH" : "THUNDER CASH — loading…"}
        </h1>
        {bootError && (
          <div style={{ color: "#e7c78a", maxWidth: 340 }}>
            <p style={{ fontSize: 14, marginBottom: 14 }} data-testid="bof-load-error">
              We couldn't reach the game right now. Please check your connection and try again.
            </p>
            <button onClick={() => window.location.reload()} data-testid="bof-retry-btn"
              style={{ background: "#ff8a1e", color: "#000", fontWeight: 800, border: "none",
                borderRadius: 999, padding: "10px 24px", fontSize: 14, cursor: "pointer" }}>
              Retry
            </button>
          </div>
        )}
      </div></div></div>
    );
  }

  return (
    <div className="bof-root tc-root" data-testid="bof-game" style={{
      backgroundImage: `linear-gradient(180deg, rgba(10,9,8,0.32), rgba(10,9,8,0.70)), url(${process.env.PUBLIC_URL}/games/thunder-cash/vault-bg.jpg)`,
      backgroundSize: "cover, cover",
      backgroundPosition: "center top, center top",
      backgroundRepeat: "no-repeat, no-repeat",
    }}>
      {loading && (
        <div className="bof-loading-screen" data-testid="bof-loading-screen"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(8,6,4,0.55), rgba(8,6,4,0.88)), url(${process.env.PUBLIC_URL}/games/thunder-cash/vault-bg.jpg)` }}>
          <div className="bof-load-embers">
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} style={{ left: `${(i * 7 + 3) % 100}%`, animationDelay: `${(i % 7) * 0.35}s`, animationDuration: `${2.4 + (i % 5) * 0.4}s` }} />
            ))}
          </div>
          <img className="bof-load-logo" src={`${ART}/logo.png`} alt="Thunder Cash" />
          <div className="bof-load-flame" aria-hidden><span /><span /><span /></div>
          <div className="bof-load-bar"><div className="bof-load-fill" style={{ width: `${loadPct}%` }} /></div>
          <div className="bof-load-pct" data-testid="bof-load-pct">{loadPct}%</div>
          <div className="bof-load-tip">Charging the reels…</div>
        </div>
      )}


      <div className="bof-rotate" data-testid="bof-rotate">
        <div className="bof-rotate-icon">📱</div>
        <h2>Please Rotate Your Phone</h2>
        <p>Thunder Cash plays in portrait mode. Turn your device upright to continue.</p>
      </div>
      <div className={`bof-mascot-wrap${boltZap ? " zap" : ""}`} data-testid="bof-mascot-wrap">
        <img className="bof-mascot" src={`${ART}/mascot.png`} alt="Thunder King" data-testid="bof-mascot" />
        <img className="bof-mascot-bolt" src={`${ART}/mascot-bolt.png`} alt="" aria-hidden="true" draggable={false} />
      </div>
      <div className="bof-stage">
        <div className="bof-header">
          <button className="bof-back-btn" data-testid="bof-back-btn" onClick={goLobby} aria-label="Back to lobby">
            <ArrowLeft size={18} /> <span>Lobby</span>
          </button>
          <img className="bof-logo" src={`${ART}/logo.png`} alt="BETOHALICS: Thunder Cash" data-testid="bof-logo" />
          <div className="bof-audio-ctrl" data-testid="bof-audio-ctrl">
            <button className="bof-audio-btn" data-testid="bof-audio-mute"
              onClick={() => { const m = !audioMuted; setAudioMuted(m); bofAudio.setMuted(m); }}
              aria-label={audioMuted ? "Unmute" : "Mute"}>
              {audioMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <button className="bof-audio-btn" data-testid="bof-audio-toggle" onClick={() => setAudioPanel((v) => !v)} aria-label="Audio settings">
              <Sliders size={16} />
            </button>
            {audioPanel && (
              <div className="bof-audio-panel" data-testid="bof-audio-panel">
                <label>Music
                  <input type="range" min="0" max="1" step="0.05" defaultValue={bofAudio.getState().musicVol}
                    data-testid="bof-audio-music" onChange={(e) => bofAudio.setMusicVol(parseFloat(e.target.value))} />
                </label>
                <label>SFX
                  <input type="range" min="0" max="1" step="0.05" defaultValue={bofAudio.getState().sfxVol}
                    data-testid="bof-audio-sfx" onChange={(e) => bofAudio.setSfxVol(parseFloat(e.target.value))} />
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="bof-jp-strip" data-testid="bof-jackpots" style={{ backgroundImage: `url(${ART}/jackpot-strip.png)`, position: "relative" }}>
          <DailySuperChip style={{ transform: "translate(-50%, -15%)" }} />
          <div className="bof-jp-values">
            {["MINI", "MINOR", "MAJOR", "MEGA"].map((t) => (
              <div className={`bof-jp-v${jackpot?.tier === t ? " hit hit-" + t.toLowerCase() : ""}`} key={t} data-testid={`bof-jp-${t.toLowerCase()}`}>{usd(jpDisplay(t))}</div>
            ))}
          </div>
        </div>

        <div className="bof-board-wrap" data-testid="bof-board">
          <div className="bof-cabinet" style={{ backgroundImage: `url(${ART}/cabinet.png)` }}>
            <div className="bof-grid">
              {Array.from({ length: 5 }).map((_, col) => {
                const wr = {};
                for (let row = 0; row < 4; row++) if (winCells.has(`${col}-${row}`)) wr[row] = true;
                return (
                  <BofReel
                    key={col}
                    ref={(el) => { reelRefs.current[col] = el; }}
                    reelIndex={col}
                    col={grid[col]}
                    winRows={wr}
                  />
                );
              })}
              <svg className="bof-lines-svg" viewBox="0 0 5 4" preserveAspectRatio="none">
                {winLines.map((pts, i) => {
                  const d = pts.map(([c, r]) => `${c + 0.5},${r + 0.5}`).join(" ");
                  return (
                    <g key={i}>
                      <polyline className="bof-line-glow" points={d} fill="none" stroke="#ff8a2a"
                        strokeWidth="0.26" strokeLinejoin="round" strokeLinecap="round" />
                      <polyline className="bof-line-main" points={d} fill="none" stroke="#ffe07a"
                        strokeWidth="0.09" strokeLinejoin="round" strokeLinecap="round" />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="tc-bonus-info" data-testid="tc-bonus-info">
            <div className="tc-bonus-title">Bonus Features</div>
            <div className="tc-bonus-row" data-testid="tc-bonus-scatter">
              <img className="tc-bonus-icon" src={symUrl("SCATTER")} alt="Scatter" draggable={false} />
              <div className="tc-bonus-text">
                <div className="tc-bonus-head"><b>3 Scatters</b> trigger Free Plays</div>
                <div className="tc-bonus-sub">Land 3 SCATTER symbols anywhere to win free spins.</div>
              </div>
            </div>
            <div className="tc-bonus-row" data-testid="tc-bonus-ball">
              <img className="tc-bonus-icon" src={symUrl("BALL")} alt="Thunder Ball" draggable={false} />
              <div className="tc-bonus-text">
                <div className="tc-bonus-head"><b>4 Thunder Balls</b> trigger the Respin Bonus</div>
                <div className="tc-bonus-sub">Land 4 POWER ORBS to launch the Thunder Respins bonus.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bof-meters" data-testid="bof-meters">
          <div className="bof-meter" style={{ backgroundImage: `url(${ART}/meter-balance.png)` }}>
            <div className="lbl">BALANCE</div>
            <div className="val" data-testid="bof-balance">{usd(balance)}</div>
            {bonus > 0 && (
              <div className="bof-bonus-badge" data-testid="bof-bonus-badge" title="Bonus funds are played first">
                <span className="bof-bonus-dot" /> BONUS {usd(bonus)}
              </div>
            )}
          </div>
          <div className="bof-meter" style={{ backgroundImage: `url(${ART}/meter-bet.png)` }}><div className="lbl">BET</div><div className="val" data-testid="bof-bet">{usd(bet)}</div></div>
          <div className="bof-meter" style={{ backgroundImage: `url(${ART}/meter-win.png)` }}><div className="lbl">WIN</div><div className="val gold" data-testid="bof-win">{usd(lastWin)}</div></div>
        </div>

        <div className="bof-controls" data-testid="bof-controls">
          <div className="bof-cluster">
            <button className="bof-imgbtn sm" style={{ backgroundImage: `url(${ART}/btn-info.png)` }} onClick={() => setShowPaytable(true)} disabled={spinning} aria-label="Paytable" data-testid="bof-info-btn" />
            <button className="bof-imgbtn sm" style={{ backgroundImage: `url(${ART}/btn-minus.png)` }} onClick={() => changeBet(-1)} disabled={spinning || betIndex === 0} aria-label="Decrease bet" data-testid="bof-bet-down" />
            <button className="bof-imgbtn sm" style={{ backgroundImage: `url(${ART}/btn-plus.png)` }} onClick={() => changeBet(1)} disabled={spinning || betIndex === BET_LEVELS.length - 1} aria-label="Increase bet" data-testid="bof-bet-up" />
            <button className="bof-imgbtn sm" style={{ backgroundImage: `url(${ART}/btn-maxbet.png)` }} onClick={maxBet} disabled={spinning} aria-label="Max bet" data-testid="bof-maxbet" />
          </div>
          <div className="bof-cluster">
            <button className={`bof-imgbtn sm bof-auto${auto ? " on" : ""}`} style={{ backgroundImage: `url(${ART}/btn-auto.png)` }} onClick={() => setAuto((a) => !a)} aria-label="Auto spin" data-testid="bof-auto-btn" />
            <button
              className={`bof-imgbtn bof-spin${spinning ? " busy" : ""}${spinning && stopArmed ? " stoppable" : ""}`}
              style={{ backgroundImage: `url(${ART}/btn-spin.png)` }}
              onClick={spinning ? requestStop : doSpin}
              disabled={!sessionId || (spinning && !stopArmed)}
              aria-label={spinning ? "Stop" : "Spin"}
              data-testid="bof-spin-btn">
              {spinning && <span className="bof-spin-stop" data-testid="bof-stop-label">STOP</span>}
            </button>
          </div>
        </div>
        <div className="bof-note">Total Win: <b data-testid="bof-total-win">{usd(totalWin)}</b></div>
        {(new URLSearchParams(window.location.search).get("qa") === "1" || !/(^|\.)betohalics\.com$/i.test(window.location.hostname)) && (
          <>
            <div className="bof-preview-bar" data-testid="bof-preview-bar">
              <span className="bof-preview-label">PREVIEW BONUS:</span>
              <button className="bof-preview-btn" data-testid="bof-preview-burning"
                onClick={() => previewBonus("burning")} disabled={spinning || !!feature}>🔥 Thunder Respins</button>
              <button className="bof-preview-btn" data-testid="bof-preview-free"
                onClick={() => previewBonus("free")} disabled={spinning || !!feature}>Free Games</button>
            </div>
            <div className="bof-preview-bar" data-testid="bof-preview-jackpots">
              <span className="bof-preview-label">JACKPOT:</span>
              {["MINI", "MINOR", "MAJOR", "MEGA"].map((t) => (
                <button key={t} className={`bof-preview-btn jp-${t.toLowerCase()}`} data-testid={`bof-preview-jp-${t.toLowerCase()}`}
                  onClick={() => runJackpot(t)} disabled={!!jackpot}>{t}</button>
              ))}
            </div>
          </>
        )}
      </div>

      {toast && <div className="bof-toast bof-gold-text" data-testid="bof-toast">{toast}</div>}

      {bigWin && (
        <div className={`bof-bigwin ${bigWin.cls}`} data-testid="bof-bigwin">
          <div className="bof-bigwin-embers">
            {BIG_WIN_EMBERS.map((e) => (
              <span key={e.id} className="bof-ember" style={{
                left: `${e.left}%`, width: e.size, height: e.size,
                animationDelay: `${e.delay}s`, animationDuration: `${e.dur}s`,
                "--bof-drift": `${e.drift}px`,
              }} />
            ))}
          </div>
          <div className="bof-bigwin-burst" />
          <div className="bof-bigwin-label bof-gold-text" data-testid="bof-bigwin-label">{bigWin.label}</div>
          <div className="bof-bigwin-amt" data-testid="bof-bigwin-amount">{usd(bigWin.shown)}</div>
        </div>
      )}

      {jackpot && (
        <div className={`bof-jackpot ${jackpot.tier.toLowerCase()}`} data-testid="bof-jackpot" onClick={() => setJackpot(null)}>
          <div className="bof-jackpot-rays" />
          <div className="bof-jackpot-burst" />
          <div className="bof-bigwin-embers">
            {BIG_WIN_EMBERS.map((e) => (
              <span key={e.id} className="bof-ember" style={{
                left: `${e.left}%`, width: e.size, height: e.size,
                animationDelay: `${e.delay}s`, animationDuration: `${e.dur}s`,
                "--bof-drift": `${e.drift}px`,
              }} />
            ))}
          </div>
          <div className="bof-jackpot-tier" data-testid="bof-jackpot-tier">{jackpot.tier}</div>
          <div className="bof-jackpot-label">JACKPOT!</div>
          <div className="bof-jackpot-amt" data-testid="bof-jackpot-amount">{usd(jackpot.shown)}</div>
        </div>
      )}

      {feature?.type === "burning" && (
        <div className="bof-overlay bof-bb" data-testid="bof-burning-overlay">
          <div className="bof-bb-top">
            <div className="bof-bb-header" style={{ backgroundImage: `url(${ART}/bb-header.png)` }}>
              <div className="bof-bb-respins" data-testid="bof-bb-respins">{feature.respins}</div>
            </div>
            <div className="bof-jp-strip bof-bb-jp" data-testid="bof-bb-jackpots" style={{ backgroundImage: `url(${ART}/jackpot-strip.png)` }}>
              <div className="bof-jp-values">
                {["MINI", "MINOR", "MAJOR", "MEGA"].map((t) => (
                  <div className={`bof-jp-v${jackpot?.tier === t ? " hit hit-" + t.toLowerCase() : ""}`} key={t} data-testid={`bof-bb-jp-${t.toLowerCase()}`}>{usd(jpDisplay(t))}</div>
                ))}
              </div>
            </div>
            <div className="bof-bb-respins-line" data-testid="bof-bb-respins-line">
              RESPINS LEFT <b>{feature.respins}</b>
            </div>
          </div>

          <div className="bof-bb-cabinet" data-testid="bof-bb-cabinet" style={{ backgroundImage: `url(${ART}/bb-cabinet.png)` }}>
            <div className="bof-bb-grid">
              {Array.from({ length: 8 }).map((_, row) =>
                Array.from({ length: 5 }).map((__, col) => {
                  const ball = feature.board.find((b) => b.col === col && b.row === row);
                  const landing = feature.landing?.has(`${col}-${row}`);
                  const activeFrom = 8 - (feature.activeRows || 0);
                  const colStopped = !feature.rolling || col < (feature.rollCol ?? 0);
                  // landed balls stay LOCKED for the rest of the bonus — never re-spin
                  const showBall = !!ball && !(feature.flew && feature.flew.has(`${col}-${row}`));
                  const settling = feature.rolling && col === (feature.settleCol ?? -1) && row >= activeFrom && !ball;
                  const rolling = feature.rolling && !colStopped && row >= activeFrom && !ball;
                  const filler = !ball && colStopped ? feature.fillers?.[`${col}-${row}`] : null;
                  return (
                    <div key={`${col}-${row}`} className="bof-bb-cell" data-testid={`bof-bb-cell-${col}-${row}`}>
                      {rolling && (
                        <div className="bof-bb-roll">
                          <div className={`bof-bb-rollstrip${settling ? " settling" : ""}`}>
                            {BB_ROLL_STRIPS[col].map((sy, k) => (
                              <img key={k} src={symUrl(sy)} alt="" draggable={false} />
                            ))}
                          </div>
                        </div>
                      )}
                      {filler && (
                        <img className="bof-bb-filler" src={symUrl(filler)} alt="" draggable={false} />
                      )}
                      {showBall && (
                        <div className={`bof-bb-ball${landing ? " landing" : ""}${ball.jackpot ? " jp-ball jp-" + ball.jackpot.toLowerCase() : ""}`} data-testid={`bof-bb-ball-${col}-${row}`}
                          style={{ backgroundImage: `url(${ART}/bb-ball.png)` }}>
                          <span className={`bof-bb-val${ball.jackpot ? " jp" : ""}`}>
                            {ball.jackpot ? ball.jackpot : usd(ball.value_x * bet)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {(() => {
              const lockedCount = Math.max(0, 8 - feature.activeRows);
              const rows = new Set();
              for (let i = 0; i < lockedCount; i++) rows.add(i);
              (feature.unlocking || []).forEach((r) => rows.add(r));
              return [...rows].sort((a, b) => a - b).map((rowIdx) => {
                const unlocking = (feature.unlocking || []).includes(rowIdx);
                return (
                  <div key={`lock-${rowIdx}`}
                    className={`bof-bb-lockrow${unlocking ? " unlocking" : ""}`}
                    data-testid={`bof-bb-lockrow-${rowIdx}`}
                    style={{ top: `calc(4.6% + ${rowIdx} * 11.3625%)`, backgroundImage: `url(${ART}/bb-locked-row.png)` }} />
                );
              });
            })()}
          </div>

          <div className="bof-bb-status" data-testid="bof-bb-status">
            {feature.total != null
              ? <span className={`bof-gold-text bof-bb-total${feature.collecting ? " collecting" : ""}`} data-testid="bof-burning-total">FEATURE WIN {usd(feature.total)}</span>
              : <span className="bof-bb-hint">Land a new Thunder Ball to reset respins</span>}
          </div>
        </div>
      )}

      {feature?.type === "free" && (
        <div className="bof-overlay bof-fg" data-testid="bof-free-overlay">
          {feature.phase === "intro" && (
            <div className="bof-fg-intro" data-testid="bof-fg-intro"
              style={{ backgroundImage: `url(${ART}/fg-intro.png)` }} />
          )}
          <img className="bof-fg-logo" src={`${ART}/logo.png`} alt="Thunder Cash" draggable={false} />
          <div className="bof-fg-top">
            <div className="bof-fg-counter" data-testid="bof-fg-counter"
              style={{ backgroundImage: `url(${ART}/fg-counter.png)` }}>
              <div className="bof-fg-window">
                <span className="bof-fg-lbl">FREE GAMES</span>
                <span className="bof-fg-count">
                  <span className="bof-fg-x" data-testid="bof-fg-current">{feature.x}</span>
                  <span className="bof-fg-of">OF</span>
                  <span className="bof-fg-y" data-testid="bof-fg-total-games">{feature.y}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="bof-fg-cabinet" data-testid="bof-fg-cabinet"
            style={{ backgroundImage: `url(${ART}/fg-cabinet.png)` }}>
            <div className="bof-fg-grid2">
              {Array.from({ length: 5 }).map((_, col) => {
                const wr = {};
                for (let row = 0; row < 8; row++) if (fgWin.cells.has(`${col}-${row}`)) wr[row] = true;
                return (
                  <BofReel
                    key={col}
                    ref={(el) => { fgReelRefs.current[col] = el; }}
                    reelIndex={col}
                    rows={8}
                    col={feature.grid?.[col]}
                    winRows={wr}
                  />
                );
              })}
              <svg className="bof-lines-svg" viewBox="0 0 5 8" preserveAspectRatio="none">
                {fgWin.lines.map((pts, i) => {
                  const d = pts.map(([c, r]) => `${c + 0.5},${r + 0.5}`).join(" ");
                  return (
                    <g key={i}>
                      <polyline className="bof-line-glow" points={d} fill="none" stroke="#ff8a2a"
                        strokeWidth="0.26" strokeLinejoin="round" strokeLinecap="round" />
                      <polyline className="bof-line-main" points={d} fill="none" stroke="#ffe07a"
                        strokeWidth="0.09" strokeLinejoin="round" strokeLinecap="round" />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="bof-fg-status" data-testid="bof-fg-status">
            {feature.phase === "win"
              ? null
              : <span className="bof-fg-runwin">WIN {usd(feature.running || 0)}</span>}
          </div>

          {feature.retrigger && (
            <div className="bof-fg-retrigger" data-testid="bof-fg-retrigger"
              style={{ backgroundImage: `url(${ART}/fg-retrigger.png)` }}>
              <span className="bof-fg-retrig-val" data-testid="bof-fg-retrigger-added">+{feature.retriggerAdded}</span>
            </div>
          )}

          {feature.total != null && (
            <div className="bof-fg-win" data-testid="bof-fg-featurewin"
              style={{ backgroundImage: `url(${ART}/fg-featurewin.png)` }}>
              <span className="bof-fg-win-val" data-testid="bof-free-total">{usd(feature.total)}</span>
            </div>
          )}
        </div>
      )}

      {showPaytable && (
        <div className="bof-overlay" data-testid="bof-paytable">
          <div className="bof-overlay-card bof-frame">
            <div className="bof-overlay-title bof-gold-text">PAYTABLE &amp; HELP</div>
            <div className="bof-pt-scroll">
            {["WILD", "SCATTER", "BALL", "CROWN", "CASH", "DIAMOND", "A", "K", "Q", "J"].map((s) => {
              const pay = config.base_paytable[s];
              return (
                <div className="bof-pt-row" key={s} data-testid={`bof-pt-${s}`}>
                  <img src={symUrl(s)} alt={s} />
                  <div className="bof-pt-name">{NAME[s]}</div>
                  <div className="bof-pt-pays">
                    {pay ? [3, 4, 5].map((n) => pay[n] > 0 && <span key={n}>{n}× <b>{pay[n]}</b></span>) :
                      s === "BALL" ? <span>4+ triggers <b>Thunder Respins</b></span> :
                      s === "SCATTER" ? <span>3 → <b>5</b> · 4 → <b>8</b> · 5+ → <b>12</b> free games</span> : null}
                  </div>
                </div>
              );
            })}
            <ul className="bof-rules">
              <li>4 or more Thunder Respins trigger the Thunder Respins Bonus.</li>
              <li>3 Scatters award 5 Free Games (4 → 8, 5+ → 12; up to {config.free_games.max_total_games}).</li>
              <li>WILD substitutes for all regular symbols.</li>
              <li>Jackpots: MINI {config.burning_balls.jackpots_x.MINI}× · MINOR {config.burning_balls.jackpots_x.MINOR}× · MAJOR {config.burning_balls.jackpots_x.MAJOR}× · MEGA {config.burning_balls.jackpots_x.MEGA}× (× bet).</li>
              <li>Maximum win capped at {config.max_win_x}× bet.</li>
            </ul>
            <div className="bof-feat-note">Pay values shown are pay-per-line units from the authoritative M10 configuration.</div>
            </div>
            <button className="bof-btn bof-pill bof-close" onClick={() => setShowPaytable(false)} data-testid="bof-paytable-close">CLOSE</button>
          </div>
        </div>
      )}
    </div>
  );
}

function randGrid8() {
  const pool = SYMS.filter((s) => s !== "J"); // J removed in Free Games per M10
  const pick = () => pool[Math.floor(Math.random() * pool.length)];
  return Array.from({ length: 5 }, () => Array.from({ length: 8 }, pick));
}
